import { useCallback, useEffect, useState } from 'react';
import {
  apercuEnvoiDocument,
  envoyerDocumentSession,
  getPlanification,
} from '../services/sessionService';
import { formatDate } from './devisUtils';

/** « 16 sept. 2026 · 17 sept. 2026 » — l'ordre vient déjà du serveur. */
const joursTexte = (jours = []) => jours.map((j) => formatDate(j)).join(' · ');

/**
 * Une journée est prête à partir quand elle a produit ses deux documents :
 * la convocation qu'on envoie, et la feuille de présence de la même séance.
 * Le serveur applique la même règle et refuserait l'envoi.
 */
function etatJournee(session) {
  const documents = session.documents || [];
  const convocation = documents.find((d) => d.type === 'convocation');
  const feuille = documents.find((d) => d.type === 'feuille_presence');

  if (!convocation) return { blocage: 'convocation à générer' };
  if (!feuille) return { convocation, blocage: 'feuille de présence à générer' };
  // Un formateur assigné reçoit sa convocation dans le même envoi : il la
  // faut aussi. Même règle que le serveur (typesRequis).
  if (session.formateur_id && !documents.some((d) => d.type === 'convocation_formateur')) {
    return { convocation, blocage: 'convocation du formateur à générer' };
  }
  return { convocation };
}

/**
 * Choix des journées à convoquer, ouvert par le bouton « Envoyer » de la page
 * Convocation.
 *
 * Un contrat porte souvent plusieurs journées, chacune avec sa convocation :
 * les envoyer une par une revenait à rouvrir la liste autant de fois. La
 * journée d'où l'on vient est cochée d'avance ; les autres sont à portée de
 * clic, et celles qui ne peuvent pas partir disent pourquoi.
 */
function EnvoiConvocations({ contrat, documentId, onClose, onEnvoye }) {
  const [sessions, setSessions] = useState([]);
  const [choisis, setChoisis] = useState(() => new Set([documentId]));
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [progression, setProgression] = useState(null);

  // L'email tel qu'il partira : le gabarit de la convocation, relu et
  // modifiable avant l'envoi. Les balises {{…}} (nom, liens de réponse)
  // sont remplies pour chaque destinataire au moment de l'envoi.
  const [apercu, setApercu] = useState(null);
  const [apercuDeplie, setApercuDeplie] = useState(false);
  const [sujet, setSujet] = useState('');
  const [texte, setTexte] = useState('');

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const res = await getPlanification(contrat.id);
      setSessions(res.data.sessions || []);
    } catch (err) {
      setErreur(err.message || 'Chargement des journées impossible.');
    } finally {
      setChargement(false);
    }
  }, [contrat.id]);

  useEffect(() => {
    charger();
  }, [charger]);

  // Le gabarit est le même pour toutes les journées (modèle « convocation ») :
  // on le lit une fois, sur le document d'où l'on vient.
  useEffect(() => {
    let vivant = true;
    apercuEnvoiDocument(documentId)
      .then((res) => {
        if (!vivant) return;
        setApercu(res.data);
        setSujet(res.data.sujet || '');
        setTexte(res.data.texte || '');
      })
      .catch(() => {});
    return () => {
      vivant = false;
    };
  }, [documentId]);

  const basculer = (id) =>
    setChoisis((prev) => {
      const suivant = new Set(prev);
      if (suivant.has(id)) suivant.delete(id);
      else suivant.add(id);
      return suivant;
    });

  /**
   * Envoi séquentiel : chaque convocation rend un PDF par participant, les
   * lancer de front saturerait le Chromium du serveur. Un échec n'arrête pas
   * les suivants — le compte rendu dit ce qui est parti.
   */
  async function envoyer() {
    const ids = [...choisis];
    setProgression({ fait: 0, total: ids.length });
    setErreur(null);

    const partis = [];
    const echecs = [];

    // Le gabarit éventuellement retouché vaut pour toutes les journées.
    const edits =
      apercu && (sujet !== apercu.sujet || texte !== apercu.texte) ? { sujet, texte } : {};

    for (const [i, id] of ids.entries()) {
      setProgression({ fait: i, total: ids.length });
      try {
        const res = await envoyerDocumentSession(id, edits);
        partis.push(res.message);
      } catch (err) {
        echecs.push(err.message || 'envoi impossible');
      }
    }

    setProgression(null);

    if (!partis.length) {
      setErreur(echecs[0] || 'Envoi impossible.');
      return;
    }

    onEnvoye(
      `${partis.length} journée${partis.length > 1 ? 's' : ''} envoyée${
        partis.length > 1 ? 's' : ''
      }${echecs.length ? ` · ${echecs.length} échec(s)` : ''} — ${partis.join(' · ')}`
    );
    onClose();
  }

  const enCours = Boolean(progression);

  return (
    <div className="devis-modal-overlay envoi-overlay" onClick={onClose}>
      <div className="devis-modal envoi-modal" onClick={(e) => e.stopPropagation()}>
        <div className="devis-modal-header">
          <div>
            <div className="devis-modal-numero">Envoyer les convocations</div>
            <div className="devis-modal-sub">
              {contrat.numero} · {contrat.societe_nom}
            </div>
          </div>
          <div className="devis-modal-header-right">
            <button
              type="button"
              className="devis-drawer-close"
              onClick={onClose}
              aria-label="Fermer"
            >
              ×
            </button>
          </div>
        </div>

        <div className="devis-modal-body">
          {chargement && <div className="devis-drawer-empty">Chargement des journées...</div>}
          {erreur && <div className="devis-alert devis-alert-erreur">{erreur}</div>}

          {!chargement && sessions.length === 0 && (
            <div className="devis-drawer-empty">Aucune journée planifiée sur ce contrat.</div>
          )}

          {!chargement &&
            sessions.map((s) => {
              const { convocation, blocage } = etatJournee(s);
              const id = convocation?.id;
              const coche = id ? choisis.has(id) : false;

              return (
                <label
                  key={s.id}
                  className={`envoi-journee${blocage ? ' envoi-journee-bloquee' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={coche}
                    disabled={Boolean(blocage) || enCours}
                    onChange={() => basculer(id)}
                  />
                  <span className="envoi-journee-corps">
                    <span className="envoi-journee-titre">{s.intitule}</span>
                    <span className="envoi-journee-meta">
                      {joursTexte(s.jours)}
                      {s.groupe_libelle ? ` · ${s.groupe_libelle}` : ''}
                      {s.lieu ? ` · ${s.lieu}` : ''}
                    </span>
                  </span>
                  <span className="envoi-journee-etat">
                    {blocage ? (
                      <span className="envoi-journee-blocage">{blocage}</span>
                    ) : convocation.date_envoi ? (
                      <span className="documents-envoi documents-envoi-parti">Envoyée</span>
                    ) : (
                      <span className="documents-envoi documents-envoi-attente">À envoyer</span>
                    )}
                  </span>
                </label>
              );
            })}
          {apercu && (
            <div style={{ marginTop: 14, borderTop: '1px solid #e2e2ea', paddingTop: 12 }}>
              {/* À qui ça part : toujours visible, avant même d'ouvrir
                  l'aperçu — on ne confirme pas un envoi sans savoir pour qui. */}
              {(apercu.destinataires || []).length > 0 && (
                <div className="devis-form-field" style={{ marginBottom: 10 }}>
                  <label>
                    Destinataire{apercu.destinataires.length > 1 ? 's' : ''} (chacun reçoit sa
                    convocation nominative)
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '6px 0' }}>
                    {apercu.destinataires.map((d) => (
                      <span key={d} className="devis-status-pill">
                        {d}
                      </span>
                    ))}
                  </div>
                  {apercu.sansEmail?.length > 0 && (
                    <div className="devis-status-detail">
                      Sans adresse email (ne recevront rien) : {apercu.sansEmail.join(', ')}
                    </div>
                  )}
                </div>
              )}
              <button
                type="button"
                className="devis-btn devis-btn-sm"
                onClick={() => setApercuDeplie((v) => !v)}
                disabled={enCours}
              >
                {apercuDeplie ? "Masquer l'email" : "Aperçu de l'email envoyé"}
              </button>
              {apercuDeplie && (
                <div style={{ marginTop: 10 }}>
                  <div className="devis-alert" style={{ marginBottom: 10 }}>
                    Les balises <code>{'{{…}}'}</code> (nom, liens de réponse) seront
                    remplies automatiquement pour chaque destinataire. Vos retouches valent
                    pour toutes les journées cochées, cet envoi seulement.
                  </div>
                  <div className="devis-form-field" style={{ marginBottom: 10 }}>
                    <label htmlFor="envoi-convocations-sujet">Objet</label>
                    <input
                      id="envoi-convocations-sujet"
                      type="text"
                      value={sujet}
                      onChange={(e) => setSujet(e.target.value)}
                      disabled={enCours}
                      style={{ width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div className="devis-form-field">
                    <label htmlFor="envoi-convocations-texte">Message</label>
                    <textarea
                      id="envoi-convocations-texte"
                      value={texte}
                      onChange={(e) => setTexte(e.target.value)}
                      disabled={enCours}
                      rows={Math.min(14, Math.max(6, texte.split('\n').length + 1))}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        fontFamily: 'inherit',
                        fontSize: 14,
                        lineHeight: 1.5,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="devis-modal-footer">
          <button type="button" className="devis-btn" onClick={onClose} disabled={enCours}>
            Annuler
          </button>
          <button
            type="button"
            className="devis-btn devis-btn-primary"
            onClick={envoyer}
            disabled={enCours || choisis.size === 0}
          >
            {enCours
              ? `Envoi ${progression.fait + 1}/${progression.total}...`
              : `Envoyer (${choisis.size})`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EnvoiConvocations;
